import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as ls from 'local-storage';
import { SubmissionError } from 'redux-form';
import { withRouter } from 'react-router';
import qs from 'qs';
import classNames from 'classnames';
import { detect } from 'detect-browser';
import bcrypt from 'bcryptjs';

import { SYNC_ACCESS_TOKEN } from 'Common/Constants';
import { clearState } from 'State/actions';
import {
  createToast,
  toastType,
  errorToast,
  toastSuccess,
} from 'Common/ToastHelper';
import {
  DONT_SHOW_REDUX_ERROR,
  STORAGE_DISTRICT_ID,
  STORAGE_FORCE_LOG_OUT,
} from 'Common/Constants';
import LoginForm from './components/LoginForm';
import { logOutUser } from 'Common/Security/SecurityHelpers';
import { token, MPWApi } from 'API/MPWApi';
import RegistrationForm from './components/RegistrationForm';
import {
  setUserInfo,
  getAccessInfo,
  setAccessInfo,
} from 'Common/Security/state/actions';
import { urlParamObj, getFiscalYear } from 'Common/GeneralHelper';
import { connect as signalRConnect } from 'API/SignalR/state/actions';
import {
  setResetPasswordStep,
  postResetPassword,
  clearPasswordError,
  setResetPasswordSuccess,
} from './state/resetpassword/actions';
import * as LoginActions from './state/login/actions';
import * as LoginSelectors from './state/selectors';
import * as ConnectionSelectors from 'State/selectors';

import { initializeNotifications } from 'Common/UI/state/actions';
import ResetPasswordForm from './components/ResetPasswordForm';
import { RESET_PASSWORD_STEP } from './helpers/constants';
import getDatabase, {
  generateDatabaseName,
  generateIdentifier,
} from 'Common/DB';
import { init as initPendo } from 'Containers/app/Pendo';
import { Scenario } from './helpers/constants';
import { RegisterFieldEnum, FieldEnum } from 'Common/FieldEnum';
import convertFileToBase64 from 'Common/Helpers/convertFileToBase64';
import { syncOfflineTransactionHistory } from 'Offline/actions';
import * as ConnectionActions from 'State/connection/actions';

import Logo from '../../styles/logos/logo_blue.png';
import '../../styles/components/login.less';

class LoginContainer extends Component {
  state = {
    scenario: null,
    values: { districtId: ls.get(STORAGE_DISTRICT_ID) },
    errors: [],
    isSubmitting: false,
  };

  componentDidUpdate(prevProps, prevState) {
    //if users got here via a redirect
    //cause session times out.Remove the notice
    //so they can go somewhere else
    this.props.setPageLevelDirty(false);

    if (errorToast(this.props, prevProps, 'resetPasswordError')) {
      this.props.clearPasswordError();
    }

    if (
      this.props.resetPasswordSuccess &&
      this.props.resetPasswordSuccess !== prevProps.resetPasswordSuccess
    ) {
      toastSuccess(this.props.resetPasswordSuccess);
      this.props.setResetPasswordSuccess(null);
    }

    if (
      this.props.loginOffline &&
      this.props.loginOffline !== prevProps.loginOffline
    ) {
      this.props.setLoginOffline(false);
      this.handleOfflineLogin(this.props.loginValues);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.state.isSubmitting &&
      !nextProps.SecurityState.accessInfoLoading &&
      this.props.SecurityState.accessInfoLoading
    ) {
      if (nextProps.SecurityState.accessInfo.length === 0) {
        createToast(
          toastType.error,
          'Error',
          'Your user account does not allow access to any pages.  ' +
            'Please contact your district administrator.',
          true
        );
        this.setState({ isSubmitting: false });
      } else {
        this.setState({ isSubmitting: false });

        const { r: redirect } = urlParamObj();
        const url = !redirect ? '/home' : decodeURIComponent(redirect);
        this.props.history.replace(url);
      }
    }
  }

  componentWillMount() {
    logOutUser(null, false);
  }

  componentDidMount() {
    if (detect().name !== 'chrome') {
      createToast(
        toastType.info,
        'Attention',
        "We're sorry, but this browser is not supported. To get the best experience, Chrome is recommended.",
        false
      );
    }

    this.props.clearState();

    const queryString = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });

    if (queryString && queryString.resetCode) {
      this.props.setResetPasswordStep(RESET_PASSWORD_STEP.Step_2_After_Email);
    }
  }

  componentWillUnmount() {
    ls.set(STORAGE_FORCE_LOG_OUT, false);
  }

  handleOfflineLogin = async (values) => {
    try {
      const {
        signalRConnect,
        setUserInfo,
        initializeNotifications,
        setAccessInfo,
        history,
        setOfflineMode,
      } = this.props;
      const identifier = generateIdentifier(values.districtId, values.username);
      const databaseName = generateDatabaseName(identifier);

      const DB = await getDatabase(databaseName);

      const { hash, userInfo, accessInfo } =
        (await DB.AccessToken.get(SYNC_ACCESS_TOKEN)) || {};

      if (!hash) {
        throw new Error(
          'In order to enable offline access, you must have logged in online at least once from this machine.'
        );
      }

      // Compare the users offline database hash with the hash created from the login password to determine if the user should be able to login offline.
      if (bcrypt.compareSync(values.password, hash)) {
        if (accessInfo.length === 0) {
          throw new Error(
            'Your user account does not allow access to any pages. Please contact your district administrator.'
          );
        }

        await setUserInfo(userInfo, hash);
        await initializeNotifications([]);
        await setAccessInfo(accessInfo);

        signalRConnect();
        setOfflineMode(true);
        history.replace('/home');
      } else {
        throw new Error('Invalid Username or Password.');
      }
    } catch (err) {
      this.setState({ isSubmitting: false });
      const message =
        err instanceof Error ? err.message : 'Invalid Username or Password.';
      createToast(toastType.error, 'Error', message, true);
    }
  };

  handleSubmit = (values) => {
    const { isOnline, isServerUp } = this.props;

    this.setState({ isSubmitting: true });

    const { districtId } = values;
    ls.set(STORAGE_DISTRICT_ID, districtId);

    if (!isOnline || !isServerUp) {
      return this.handleOfflineLogin(values);
    }

    return new Promise((resolve, reject) => {
      MPWApi.loginApi
        .postLogin(values)
        .then(async (result) => {
          const salt = bcrypt.genSaltSync(10);
          const hash = bcrypt.hashSync(values.password, salt);
          const {
            Expires,
            Token,
            RefreshToken,
            Notifications,
            ...userInfo
          } = result.data.Data;

          token.update({
            tokenExpirationUTC: Expires,
            accessToken: Token,
            refreshToken: RefreshToken,
          });

          await this.props.setUserInfo({ Notifications, ...userInfo }, hash);
          await this.props.getAccessInfo();
          this.props.initializeNotifications(Notifications);
          this.props.syncOfflineTransactionHistory();
          // this.props.initPendo();
          
          return resolve();
        })
        .catch((error) => {
          this.setState({ isSubmitting: false });
          if (error && error.response && error.response.status === 429) {
            createToast(
              toastType.error,
              'Error',
              'Invalid Username or Password.',
              true
            );
            reject(new SubmissionError({ _error: DONT_SHOW_REDUX_ERROR }));
            return;
          }
          if (!error || !error.response || !error.response.data) {
            createToast(
              toastType.error,
              'Error',
              'An error has occurred.  Unable to log in.',
              true
            );
            resolve();
            return;
          }

          const errors = error.response.data.Errors[0];
          if (errors.Scenario != null) {
            //Set state with values to be used on the next page
            const { username, password } = values;
            //Email is returned
            const { Data } = error.response.data;

            switch (errors.Scenario) {
              case Scenario.NeedsRegistration:
                this.setState({
                  scenario: Scenario.NeedsRegistration,
                  values: {
                    districtId: ls.get(STORAGE_DISTRICT_ID),
                    username,
                    oldPassword: password,
                    email: Data,
                  },
                });
                resolve();
                break;
              case Scenario.FirstRegistration:
                this.setState({
                  scenario: Scenario.FirstRegistration,
                  values: {
                    districtId: ls.get(STORAGE_DISTRICT_ID),
                    username,
                    oldPassword: password,
                    email: Data,
                    fiscalYear: getFiscalYear(new Date()),
                  },
                });
                resolve();
                break;
              default:
                resolve();
            }
          } else {
            //Throw redux form error
            if (
              error &&
              error.response &&
              error.response.data &&
              error.response.data.Errors.length > 0
            ) {
              const errorMsg = error.response.data.Errors[0];
              createToast(
                toastType.error,
                errorMsg.Title,
                errorMsg.Message,
                true
              );
              reject(
                new SubmissionError({
                  _error: DONT_SHOW_REDUX_ERROR,
                })
              );
            }
          }
        });
    });
  };

  handleRegistration = async (values) => {
    this.setState({ isSubmitting: true });
    const { scenario } = this.state;
    const { districtId, [RegisterFieldEnum.NewUsername]: newUsername } = values;
    let { [RegisterFieldEnum.LicenseFile]: licenseFile } = values;
    ls.set(STORAGE_DISTRICT_ID, districtId);

    if (licenseFile && licenseFile.length) {
      licenseFile = await convertFileToBase64(licenseFile[0]);
    }

    return new Promise((resolve, reject) => {
      MPWApi.loginApi
        .postRegister({ ...values, licenseFile })
        .then((result) => {
          createToast(
            toastType.success,
            'Registration Sucessful',
            'Please log in with your new credentials.',
            true
          );

          const optional = {};

          if (scenario === Scenario.FirstRegistration) {
            optional.values = {
              ...this.state.values,
              [FieldEnum.Username]: newUsername,
            };
          }

          this.setState({
            scenario: null,
            isSubmitting: false,
            ...optional,
          });
          resolve();
        })
        .catch((error) => {
          this.setState({ isSubmitting: false });
          if (!error.response) {
            createToast(toastType.error, 'Error', 'An error has occured', true);

            reject(new SubmissionError({ _error: DONT_SHOW_REDUX_ERROR }));
          } else {
            const errors = error.response.data.Errors;

            if (errors && errors.length > 0) {
              createToast(
                toastType.error,
                'Error',
                errors[0].Message || errors[0].Title || '',
                true
              );

              reject(new SubmissionError({ _error: DONT_SHOW_REDUX_ERROR }));
            }
          }
        });
    });
  };

  handleResetPassword = (values) => {
    const queryString = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });

    const data = {
      ...values,
      ...queryString,
    };
    this.props.postResetPassword(data, this.props.resetPasswordStep);
  };

  renderForms = () => {
    const { scenario, values, isSubmitting } = this.state;
    const {
      GlobalUIState,
      setResetPasswordStep,
      isOnline,
      isServerUp,
      setShowResetPassword,
      resetPasswordStep,
      history,
      resetPasswordLoading,
    } = this.props;

    if (
      scenario === Scenario.NeedsRegistration ||
      scenario === Scenario.FirstRegistration
    ) {
      return (
        <RegistrationForm
          initialValues={values}
          onSubmit={this.handleRegistration}
          isSubmitting={isSubmitting}
          ui={GlobalUIState}
          isFirstRegistration={scenario === Scenario.FirstRegistration}
        />
      );
    }

    if (resetPasswordStep != null) {
      return (
        <ResetPasswordForm
          onSubmit={this.handleResetPassword}
          ui={GlobalUIState}
          initialValues={{ districtId: ls.get(STORAGE_DISTRICT_ID) }}
          setShowResetPassword={setShowResetPassword}
          setResetPasswordStep={setResetPasswordStep}
          resetPasswordStep={resetPasswordStep}
          history={history}
          resetPasswordLoading={resetPasswordLoading}
        />
      );
    }

    return (
      <LoginForm
        initialValues={values}
        onSubmit={this.handleSubmit}
        isSubmitting={isSubmitting}
        ui={GlobalUIState}
        setResetPasswordStep={setResetPasswordStep}
        offline={!isOnline || !isServerUp}
      />
    );
  };

  renderResetPassword = () => {};

  renderHeader = () => {
    let headerMessage = '';
    const { scenario } = this.state;

    if (
      scenario === Scenario.NeedsRegistration ||
      scenario === Scenario.FirstRegistration
    ) {
      headerMessage = 'Please register for an account.';
    } else if (
      this.props.resetPasswordStep &&
      this.props.resetPasswordStep === RESET_PASSWORD_STEP.Step_1_Before_Email
    ) {
      headerMessage = 'Reset Password';
    } else if (
      this.props.resetPasswordStep &&
      this.props.resetPasswordStep === RESET_PASSWORD_STEP.Step_2_After_Email
    ) {
      headerMessage = 'Enter New Password';
    } else {
      headerMessage = 'Welcome! Please log in.';
    }

    return <div id="loginHeader">{headerMessage}</div>;
  };

  render() {
    const { keyboardActive, keyboardDisplay } = this.props.GlobalUIState;

    return (
      <div id="loginBack">
        <div
          id="loginContainer"
          className={classNames({
            'keyboard--wrapper-padding': keyboardActive && keyboardDisplay,
          })}
        >
          <div id="loginForm">
            <img id="loginLogo" src={Logo} alt="logo" />
            {this.renderHeader()}
            {this.renderForms()}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { SecurityState, GlobalUIState, LoginState } = state;
  const { resetPassword, login } = LoginState;
  return {
    SecurityState,
    GlobalUIState,
    showResetPassword: resetPassword.showResetPassword,
    resetPasswordLoading: resetPassword.resetPasswordLoading,
    resetPasswordError: resetPassword.resetPasswordError,
    resetPasswordStep: resetPassword.resetPasswordStep,
    resetPasswordSuccess: resetPassword.resetPasswordSuccess,
    isOnline: ConnectionSelectors.getOnline(state),
    isServerUp: ConnectionSelectors.getServerUp(state),
    loginOffline: login.loginOffline,
    loginValues: LoginSelectors.getLoginFormValues(state),
  };
};

const mapDispatchToProps = {
  setUserInfo,
  getAccessInfo,
  clearState,
  initializeNotifications,
  setResetPasswordStep,
  postResetPassword,
  clearPasswordError,
  setResetPasswordSuccess,
  setAccessInfo,
  initPendo,
  signalRConnect,
  setLoginOffline: LoginActions.setLoginOffline,
  syncOfflineTransactionHistory,
  setOfflineMode: ConnectionActions.setOfflineMode,
};

export const Login = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LoginContainer)
);



// WEBPACK FOOTER //
// ./src/pages/login/Login.js